<template>
    <div class="form-container">
        <AddressField
            :name="name"
            :label="label"
            :searchName="searchName"
            searchField="StreetName"
            :hideSearchFieldOnManual="true"
            :searchLabel="searchLabel"
            :addressFields="addressFields"
            :addressType="addressType"
            :customErrorMessage="errorMessage"
            :validationRules="validationRules"
            :searchType="'predictive'"
        />
    </div>
</template>

<script>
import { useStore } from 'vuex'

export default {
    // static settings to initialize step (not reactive inside this file!)
    stepDefinition() {
        return {
            name: 'currentAddress',
            hideContinue: true,
            stringOnSummary: true,
            selected: null,
            manual: false,
            supportsSearch: true,
            route: {
                relativeUrl: `current-address`,
                name: 'apply.address.currentAddress',
                props: { addressType: 'current', formSection: 'address' },
                meta: {
                    title: 'form.sections.address.currentAddress.heading',
                },
            },
            fields: {
                currentAddressStreetNumber: {
                    value: null,
                    mappedKey: 'street_number',
                    validationRules: {
                        required: true,
                        min: 1,
                        max: 50,
                    },
                },
                currentAddressStreetName: {
                    value: null,
                    mappedKey: 'street_name',
                    validationRules: {
                        required: true,
                        min: 2,
                        max: 50,
                    },
                },
                currentAddressUnitNumber: {
                    value: null,
                    mappedKey: 'unit',
                    validationRules: {
                        max: 50,
                    },
                },
                currentAddressSuburb: {
                    value: null,
                    mappedKey: 'suburb',
                    validationRules: {
                        required: true,
                        max: 50,
                    },
                },
                currentAddressState: {
                    value: null,
                    mappedKey: 'state',
                    validationRules: {
                        required: true,
                        max: 50,
                    },
                },
                currentAddressPostcode: {
                    value: null,
                    mappedKey: 'postcode',
                    validationRules: {
                        required: true,
                    },
                },
            },
        }
    },

    setup() {
        const store = useStore()

        return {
            store,
        }
    },
    created() {
        this.emitter.emit('stepLoaded')

        this.addressFields.StreetNumber.validationValue = this.streetNumberValue
        this.addressFields.StreetName.validationValue = this.streetNameValue
        this.addressFields.UnitNumber.validationValue = this.unitNumberValue
        this.addressFields.Suburb.validationValue = this.suburbValue
        this.addressFields.State.validationValue = this.stateValue
        this.addressFields.Postcode.validationValue = this.postcodeValue

        this.addressFields.StreetNumber.validationRules = this.store.getters.fieldValidationRules(
            `${this.addressType}AddressStreetNumber`,
        )
        this.addressFields.StreetName.validationRules = this.store.getters.fieldValidationRules(
            `${this.addressType}AddressStreetName`,
        )
        this.addressFields.UnitNumber.validationRules = this.store.getters.fieldValidationRules(
            `${this.addressType}AddressUnitNumber`,
        )
        this.addressFields.Suburb.validationRules = this.store.getters.fieldValidationRules(
            `${this.addressType}AddressSuburb`,
        )
        this.addressFields.State.validationRules = this.store.getters.fieldValidationRules(
            `${this.addressType}AddressState`,
        )
        this.addressFields.Postcode.validationRules = this.store.getters.fieldValidationRules(
            `${this.addressType}AddressPostcode`,
        )

        this.emitter.all.delete('addressField:selected')
        this.emitter.on('addressField:selected', async () => {
            await this.clearFields()
        })
    },

    props: ['addressType', 'formSection'],
    data() {
        return {
            name: `${this.addressType}Address`,
            label: this.$t(`form.sections.address.${this.addressType}Address.label`),
            errorMessage: this.$t('form.fields.postcode.errorMessage'),
            validationRules: {
                required: true,
            },
            tooltip: null,
            searchName: `${this.addressType}AddressStreetName`,
            searchLabel: this.$t('form.fields.address.labels.streetAddress'),
            addressFields: {
                StreetNumber: {
                    name: `${this.addressType}AddressStreetNumber`,
                    label: this.$t('form.fields.address.labels.streetNumber'),
                    selectLabel: this.$t('form.fields.address.labels.streetNumberSelected'),
                    icon: null,
                    validationRules: null,
                    validationValue: null,
                    errorMessage: this.$t('form.fields.streetNumber.errorMessage'),
                    class: 'col-3',
                },
                StreetName: {
                    name: `${this.addressType}AddressStreetName`,
                    label: this.$t('form.fields.address.labels.streetName'),
                    selectLabel: this.$t('form.fields.address.labels.streetNameSelected'),
                    icon: null,
                    validationRules: null,
                    validationValue: null,
                    errorMessage: this.$t('form.fields.streetName.errorMessage'),
                    sameLineAsLast: true,
                    class: 'col pl-3',
                },
                UnitNumber: {
                    name: `${this.addressType}AddressUnitNumber`,
                    label: this.$t('form.fields.address.labels.unitNumber'),
                    icon: null,
                    validationRules: null,
                    validationValue: null,
                    errorMessage: this.$t('form.fields.unitNumber.errorMessage'),
                },
                Suburb: {
                    name: `${this.addressType}AddressSuburb`,
                    label: this.$t('form.fields.suburb.label'),
                    icon: null,
                    validationRules: null,
                    validationValue: null,
                    errorMessage: this.$t('form.fields.suburb.errorMessage'),
                },
                State: {
                    name: `${this.addressType}AddressState`,
                    label: this.$t('form.fields.state.label'),
                    icon: null,
                    validationRules: null,
                    validationValue: null,
                    errorMessage: this.$t('form.fields.state.errorMessage'),
                    AutoCompleteField: true,
                },
                Postcode: {
                    name: `${this.addressType}AddressPostcode`,
                    label: this.$t('form.fields.address.labels.postcode'),
                    icon: null,
                    validationRules: null,
                    validationValue: null,
                    errorMessage: this.$t('form.fields.postcode.errorMessage'),
                },
            },
            clearFieldSteps: [`${this.addressType}AddressDuration`],
        }
    },
    computed: {
        streetNumberValue: function () {
            return this.store.getters.fieldValue(`${this.addressType}AddressStreetNumber`)
        },
        streetNameValue: function () {
            return this.store.getters.fieldValue(`${this.addressType}AddressStreetName`)
        },
        postcodeValue: function () {
            return this.store.getters.fieldValue(`${this.addressType}AddressPostcode`)
        },
        unitNumberValue: function () {
            return this.store.getters.fieldValue(`${this.addressType}AddressUnitNumber`)
        },
        suburbValue: function () {
            return this.store.getters.fieldValue(`${this.addressType}AddressSuburb`)
        },
        stateValue: function () {
            return this.store.getters.fieldValue(`${this.addressType}AddressState`)
        },
    },
    watch: {
        streetNumberValue: function (value) {
            this.addressFields.StreetNumber.validationValue = value
        },
        streetNameValue: function (value) {
            this.addressFields.StreetName.validationValue = value
        },
        postcodeValue: function (value) {
            this.addressFields.Postcode.validationValue = value
        },
        unitNumberValue: function (value) {
            this.addressFields.UnitNumber.validationValue = value
        },
        suburbValue: function (value) {
            this.addressFields.Suburb.validationValue = value
        },
        stateValue: function (value) {
            this.addressFields.State.validationValue = value
        },
    },
    methods: {
        async clearFields() {
            if (this.addressType === 'current') {
                await this.store.dispatch('clearSteps', this.clearFieldSteps)
            }
        },
    },
}
</script>
